import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import * as Cesium from 'cesium';
import 'cesium/Build/Cesium/Widgets/widgets.css';
import { usePostHog } from '@posthog/react';
import landscopeBanner from '../assets/landscope-banner.svg';
import { AccountModal } from './AccountModal';
import { LoginModal } from './LoginModal';
import { getEntryAttribution } from '../utils/entryAttribution';
const BING_ARIAL_ASSET_ID = 2;
const GLOBE_SPIN_RADIANS_PER_TICK = 0.0005;
// Full-screen entry wall shown to every unauthenticated visitor. There is no
// way past it without a full account: exploring demos, creating projects, and
// everything else lives behind login. A bare interactive Cesium globe spins
// behind the card — the visitor can grab and explore it around the card.
export const EntryGate = () => {
    const posthog = usePostHog();
    const [showAccountModal, setShowAccountModal] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const capturedRef = useRef(false);
    const globeContainerRef = useRef(null);
    useEffect(() => {
        if (capturedRef.current) {
            return;
        }
        capturedRef.current = true;
        const entry = getEntryAttribution();
        posthog === null || posthog === void 0 ? void 0 : posthog.capture('entry_gate_viewed', {
            entry_referrer: entry.referrer,
            entry_landing_path: entry.landingPath,
            entry_utm_source: entry.utmSource,
            entry_utm_medium: entry.utmMedium,
            entry_utm_campaign: entry.utmCampaign,
        });
    }, [posthog]);
    useEffect(() => {
        const container = globeContainerRef.current;
        if (!container) {
            return;
        }
        let cancelled = false;
        let viewer;
        let removeSpin;
        try {
            viewer = new Cesium.Viewer(container, {
                terrainProvider: new Cesium.EllipsoidTerrainProvider(),
                homeButton: false,
                sceneModePicker: false,
                baseLayerPicker: false,
                navigationHelpButton: false,
                geocoder: false,
                infoBox: false,
                selectionIndicator: false,
                animation: false,
                timeline: false,
                fullscreenButton: false,
                vrButton: false,
                orderIndependentTranslucency: false,
            });
            viewer.resolutionScale = window.devicePixelRatio;
            viewer.imageryLayers.removeAll();
            viewer.cesiumWidget.creditContainer.style.display = 'none';
            Cesium.Ion.defaultAccessToken = '';
            Cesium.Ion.defaultAccessToken = REACT_APP_CESIUM_ION_TOKEN;
            viewer.camera.setView({
                destination: Cesium.Cartesian3.fromDegrees(0.0, 0.0, 13000000),
                orientation: {
                    heading: Cesium.Math.toRadians(0.0),
                    pitch: Cesium.Math.toRadians(-90.0),
                    roll: 0.0,
                },
            });
            viewer.scene.globe.enableLighting = true;
            Cesium.IonImageryProvider.fromAssetId(BING_ARIAL_ASSET_ID, {
                accessToken: REACT_APP_CESIUM_ION_TOKEN,
            })
                .then((imageryProvider) => {
                if (!cancelled && viewer && !viewer.isDestroyed()) {
                    viewer.imageryLayers.addImageryProvider(imageryProvider);
                }
            })
                .catch((err) => {
                console.error('EntryGate: failed to load globe imagery:', err);
            });
            // Slow ambient spin until the visitor grabs the globe themselves.
            let spinning = true;
            const stopSpin = () => {
                spinning = false;
            };
            viewer.canvas.addEventListener('pointerdown', stopSpin);
            const removeTick = viewer.clock.onTick.addEventListener(() => {
                if (spinning && viewer && !viewer.isDestroyed()) {
                    viewer.scene.camera.rotateRight(GLOBE_SPIN_RADIANS_PER_TICK);
                }
            });
            removeSpin = () => {
                removeTick();
                viewer === null || viewer === void 0 ? void 0 : viewer.canvas.removeEventListener('pointerdown', stopSpin);
            };
        }
        catch (err) {
            console.error('EntryGate: failed to initialize globe backdrop:', err);
        }
        return () => {
            cancelled = true;
            removeSpin === null || removeSpin === void 0 ? void 0 : removeSpin();
            if (viewer && !viewer.isDestroyed()) {
                viewer.destroy();
            }
        };
    }, []);
    return (_jsxs("div", { className: "relative w-full h-screen overflow-hidden", style: { backgroundColor: 'var(--charcoal)' }, children: [_jsx("div", { ref: globeContainerRef, className: "absolute inset-0" }), _jsx("div", { className: "absolute inset-0 pointer-events-none", style: { background: 'radial-gradient(ellipse at center, rgba(0,0,0,0) 40%, rgba(0,0,0,0.55) 100%)' } }), _jsxs("div", { className: "absolute inset-0 flex flex-col items-center justify-center px-6 pointer-events-none", children: [_jsxs("div", { className: "w-full max-w-xl rounded-3xl shadow-2xl overflow-hidden pointer-events-auto", style: { backgroundColor: 'var(--charcoal)', border: '2px solid var(--charcoal)' }, children: [_jsxs("div", { className: "flex flex-col items-center gap-4 p-8", style: { backgroundColor: 'var(--forest)' }, children: [_jsx("img", { src: landscopeBanner, alt: "LandScope", className: "h-12" }), _jsx("p", { className: "text-center text-xs font-semibold uppercase tracking-widest", style: { color: 'var(--sand)', opacity: 0.8 }, children: "For the people & teams who design, develop, and steward the land" }), _jsx("h1", { className: "text-center text-lg font-semibold", style: { color: 'var(--sand)' }, children: "Design with the land, not against it." }), _jsx("p", { className: "text-center text-sm", style: { color: 'var(--sand)' }, children: "Skip days of GIS and static PDFs. LandScope is a shared, interactive map where land pros and serious landowners stack layers, sketch plans, and make land\u2011smart decisions in minutes, no GIS required." })] }), _jsxs("div", { className: "flex flex-col gap-3 p-8", style: { backgroundColor: 'var(--sand)' }, children: [_jsx("button", { onClick: () => setShowAccountModal(true), className: "w-full py-3 rounded-lg font-semibold transition-all duration-200 hover:opacity-90 hover:shadow-lg", style: { backgroundColor: 'var(--forest)', color: 'var(--sand)' }, children: "Create Free Account" }), _jsx("button", { onClick: () => setShowLoginModal(true), className: "w-full py-3 rounded-lg font-semibold transition-all duration-200", style: {
                                            backgroundColor: 'transparent',
                                            color: 'var(--forest)',
                                            border: '2px solid var(--forest)',
                                        }, onMouseEnter: (e) => {
                                            e.currentTarget.style.backgroundColor = 'var(--forest)';
                                            e.currentTarget.style.color = 'var(--sand)';
                                        }, onMouseLeave: (e) => {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                            e.currentTarget.style.color = 'var(--forest)';
                                        }, children: "Log In" }), _jsxs("p", { className: "text-center text-xs mt-1", style: { color: 'var(--charcoal)' }, children: ["Instant free demo on your site (where data is available).", _jsx("br", {}), "No card required."] })] })] }), _jsxs("div", { className: "flex gap-4 mt-6 text-xs pointer-events-auto", children: [_jsx("a", { href: "/privacy-policy", className: "hover:underline", style: { color: 'var(--sand)' }, children: "Privacy Policy" }), _jsx("a", { href: "/terms-of-service", className: "hover:underline", style: { color: 'var(--sand)' }, children: "Terms of Service" })] })] }), _jsx(AccountModal, { isOpen: showAccountModal, onClose: () => setShowAccountModal(false), onSwitchToLogin: () => {
                    setShowAccountModal(false);
                    setShowLoginModal(true);
                } }), _jsx(LoginModal, { isOpen: showLoginModal, onClose: () => setShowLoginModal(false) })] }));
};
